import {translate} from '@docusaurus/Translate';
import type {ReactNode} from 'react';
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {
  AgentApiError,
  chat,
  getAgentErrorMessage,
  submitFeedback as submitAgentFeedback,
} from '../../services/agent-api';
import type {FeedbackRating, FeedbackReason} from '../../services/agent-api';
import {readAssistantConsent, writeAssistantConsent} from './assistantConsent';
import type {
  ConversationMessage,
  ConversationState,
  MessageFeedback,
  PageContext,
  StoredConversation,
} from './assistantConversation';
import {
  CONVERSATION_STORAGE_KEY,
  CONVERSATION_STORAGE_VERSION,
  MAX_QUESTION_LENGTH,
  createAssistantId,
  emptyConversation,
  restoreConversation,
} from './assistantConversation';
import type {ConversationRecord} from './assistantHistory';
import {
  readAssistantHistory,
  removeHistoryConversation,
  toConversationRecord,
  upsertHistoryConversation,
  writeAssistantHistory,
} from './assistantHistory';

export type {ConversationMessage, MessageFeedback, PageContext};
export {MAX_QUESTION_LENGTH};

type AssistantContextValue = ConversationState & {
  isHydrated: boolean;
  hasConsented: boolean;
  isSending: boolean;
  validationError: string;
  liveMessage: string;
  history: ConversationRecord[];
  setDraft: (draft: string) => void;
  setSourceContext: (context: PageContext) => void;
  acceptConsent: () => void;
  sendDraft: () => Promise<void>;
  sendSuggestion: (question: string) => Promise<void>;
  retryMessage: (messageId: string) => Promise<void>;
  cancelRequest: () => void;
  resetConversation: () => void;
  openConversation: (localId: string) => void;
  deleteConversation: (localId: string) => void;
  sendFeedback: (
    messageId: string,
    rating: FeedbackRating,
    reason?: FeedbackReason,
  ) => Promise<void>;
  retryFeedback: (messageId: string) => Promise<void>;
};

const initialState: ConversationState = {
  localId: '',
  sessionId: null,
  messages: [],
  draft: '',
  sourceContext: null,
};

const AssistantContext = createContext<AssistantContextValue | null>(null);

function persistHistory(conversations: ConversationRecord[]): ConversationRecord[] {
  return writeAssistantHistory(conversations);
}

function failPendingMessages(state: ConversationState): ConversationState {
  if (!state.messages.some((message) => message.status === 'pending')) {
    return state;
  }

  return {
    ...state,
    messages: state.messages
      .filter((message) => message.role !== 'assistant' || message.status !== 'pending')
      .map((message) =>
        message.status === 'pending'
          ? {...message, status: 'failed' as const, error: translate({message: '请求已取消。'})}
          : message,
      ),
  };
}

function historySignature(record: ConversationRecord): string {
  return [
    record.sessionId ?? '',
    record.draft,
    record.title,
    record.messages
      .map((message) => `${message.id}:${message.status}:${message.feedback?.status ?? ''}`)
      .join(','),
  ].join('|');
}

function persistActiveConversation(conversation: ConversationState) {
  const snapshot: StoredConversation = {
    version: CONVERSATION_STORAGE_VERSION,
    ...conversation,
  };

  try {
    window.sessionStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Ignore private-mode, disabled-storage, and quota errors.
  }
}

export function AssistantProvider({children}: {children: ReactNode}): ReactNode {
  const [conversation, setConversation] = useState<ConversationState>(initialState);
  const [history, setHistory] = useState<ConversationRecord[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [liveMessage, setLiveMessage] = useState('');
  const conversationRef = useRef(conversation);
  const historyRef = useRef(history);
  const requestControllerRef = useRef<AbortController | null>(null);

  conversationRef.current = conversation;
  historyRef.current = history;

  const archiveConversation = useCallback((state: ConversationState) => {
    const record = toConversationRecord(state);
    if (!record) {
      return historyRef.current;
    }

    const existing = historyRef.current.find((item) => item.localId === record.localId);
    const sameContent = existing ? historySignature(existing) === historySignature(record) : false;
    const sameContext =
      existing?.sourceContext?.path === record.sourceContext?.path &&
      existing?.sourceContext?.title === record.sourceContext?.title;
    if (existing && sameContent && sameContext) {
      return historyRef.current;
    }

    const nextRecord =
      existing && sameContent ? {...record, updatedAt: existing.updatedAt} : record;
    const next = persistHistory(upsertHistoryConversation(historyRef.current, nextRecord));
    historyRef.current = next;
    setHistory(next);
    return next;
  }, []);

  useEffect(() => {
    try {
      setHasConsented(readAssistantConsent());
      const storedHistory = readAssistantHistory();
      historyRef.current = storedHistory;
      setHistory(storedHistory);

      const storedValue = window.sessionStorage.getItem(CONVERSATION_STORAGE_KEY);
      let nextConversation: ConversationState | null = null;
      if (storedValue) {
        const restored = restoreConversation(JSON.parse(storedValue) as unknown);
        if (restored) {
          const localId =
            restored.localId ||
            storedHistory.find((item) => item.sessionId && item.sessionId === restored.sessionId)
              ?.localId ||
            createAssistantId('conversation');
          nextConversation = {...restored, localId};
        } else {
          window.sessionStorage.removeItem(CONVERSATION_STORAGE_KEY);
        }
      }
      if (!nextConversation) {
        nextConversation = emptyConversation();
      }
      conversationRef.current = nextConversation;
      setConversation(nextConversation);
    } catch {
      // Storage is optional. The assistant remains usable with in-memory state.
    }

    if (!conversationRef.current.localId) {
      const fallback = emptyConversation();
      conversationRef.current = fallback;
      setConversation(fallback);
    }
    setIsHydrated(true);
  }, []);

  const historyPersistKey = `${conversation.localId}:${conversation.sessionId ?? ''}:${conversation.messages
    .map((message) => `${message.id}:${message.status}:${message.feedback?.status ?? ''}`)
    .join(',')}`;

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    persistActiveConversation(conversation);
  }, [conversation, isHydrated]);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    archiveConversation(conversationRef.current);
  }, [archiveConversation, historyPersistKey, isHydrated]);

  useEffect(
    () => () => {
      requestControllerRef.current?.abort();
    },
    [],
  );

  const setDraft = useCallback((draft: string) => {
    setConversation((current) => ({...current, draft}));
    setValidationError('');
  }, []);

  const setSourceContext = useCallback((context: PageContext) => {
    setConversation((current) => {
      if (
        current.sourceContext?.title === context.title &&
        current.sourceContext?.path === context.path
      ) {
        return current;
      }

      return {...current, sourceContext: context};
    });
  }, []);

  const acceptConsent = useCallback(() => {
    writeAssistantConsent();
    setHasConsented(true);
    setLiveMessage(translate({message: '已同意智能助理使用许可，可以开始提问。'}));
  }, []);

  const abortActiveRequest = useCallback(() => {
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    setIsSending(false);
  }, []);

  const sendQuestion = useCallback(
    async (rawQuestion: string, retryId?: string) => {
      if (!hasConsented) {
        setValidationError(translate({message: '请先阅读并同意智能助理使用许可。'}));
        return;
      }

      if (requestControllerRef.current) {
        setValidationError(translate({message: '请等待当前回答完成，或先取消本次请求。'}));
        return;
      }

      const question = rawQuestion.trim();
      if (!question) {
        setValidationError(translate({message: '请输入想了解的问题。'}));
        return;
      }
      if (question.length > MAX_QUESTION_LENGTH) {
        setValidationError(translate({message: '问题最多 {value0} 个字符。'}, {value0: MAX_QUESTION_LENGTH}));
        return;
      }

      const snapshot = conversationRef.current;
      const pageContext = retryId
        ? snapshot.messages.find((message) => message.id === retryId)?.pageContext ??
          snapshot.sourceContext ??
          undefined
        : snapshot.sourceContext ?? undefined;
      const userMessageId = retryId ?? createAssistantId('question');
      const assistantMessageId = createAssistantId('answer');
      const assistantCreatedAt = new Date().toISOString();
      const controller = new AbortController();

      requestControllerRef.current = controller;
      setIsSending(true);
      setValidationError('');
      setLiveMessage(translate({message: '智能助理正在查找文档并组织回答。'}));
      setConversation((current) => {
        const nextMessage: ConversationMessage = {
          id: userMessageId,
          role: 'user',
          content: question,
          status: 'pending',
          createdAt: new Date().toISOString(),
          pageContext,
        };

        return {
          ...current,
          draft: '',
          messages: retryId
            ? current.messages.map((message) =>
                message.id === retryId ? {...nextMessage, createdAt: message.createdAt} : message,
              )
            : [...current.messages, nextMessage],
        };
      });

      try {
        const response = await chat(
          {
            message: question,
            ...(snapshot.sessionId ? {session_id: snapshot.sessionId} : {}),
            ...(pageContext?.title ? {page_title: pageContext.title} : {}),
            ...(pageContext?.path ? {page_path: pageContext.path} : {}),
          },
          controller.signal,
          {
            onSession: ({session_id, message_id}) => {
              if (requestControllerRef.current !== controller) {
                return;
              }

              setConversation((current) => {
                const assistantMessage: ConversationMessage = {
                  id: assistantMessageId,
                  role: 'assistant',
                  content: '',
                  status: 'pending',
                  createdAt: assistantCreatedAt,
                  messageId: message_id,
                };

                return {
                  ...current,
                  sessionId: session_id,
                  messages: current.messages.some((message) => message.id === assistantMessageId)
                    ? current.messages
                    : [...current.messages, assistantMessage],
                };
              });
              setLiveMessage(translate({message: '智能助理正在流式生成回答。'}));
            },
            onChunk: (content) => {
              if (requestControllerRef.current !== controller) {
                return;
              }

              setConversation((current) => ({
                ...current,
                messages: current.messages.map((message) =>
                  message.id === assistantMessageId
                    ? {...message, content: message.content + content}
                    : message,
                ),
              }));
            },
            onQuality: ({quality_status, quality_reason}) => {
              if (requestControllerRef.current !== controller) {
                return;
              }

              setConversation((current) => ({
                ...current,
                messages: current.messages.map((message) =>
                  message.id === assistantMessageId
                    ? {
                        ...message,
                        qualityStatus: quality_status,
                        qualityReason: quality_reason,
                      }
                    : message,
                ),
              }));
            },
          },
        );

        if (requestControllerRef.current !== controller) {
          return;
        }

        const assistantMessage: ConversationMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: response.reply,
          status: 'complete',
          createdAt: assistantCreatedAt,
          messageId: response.message_id,
          qualityStatus: response.quality_status,
          qualityReason: response.quality_reason,
          recommendedQuestions: response.recommended_questions,
          tokenUsage: response.token_usage,
        };

        setConversation((current) => ({
          ...current,
          sessionId: response.session_id,
          messages: current.messages.some((message) => message.id === assistantMessageId)
            ? current.messages.map((message) => {
                if (message.id === userMessageId) {
                  return {...message, status: 'complete' as const};
                }
                return message.id === assistantMessageId ? assistantMessage : message;
              })
            : [
                ...current.messages.map((message) =>
                  message.id === userMessageId
                    ? {...message, status: 'complete' as const}
                    : message,
                ),
                assistantMessage,
              ],
        }));
        setLiveMessage(translate({message: '智能助理已返回回答。'}));
      } catch (error) {
        if (requestControllerRef.current !== controller) {
          return;
        }

        const errorMessage = getAgentErrorMessage(error);
        setConversation((current) => ({
          ...current,
          draft: question,
          messages: current.messages
            .filter((message) => message.id !== assistantMessageId)
            .map((message) =>
              message.id === userMessageId
                ? {...message, status: 'failed' as const, error: errorMessage}
                : message,
            ),
        }));
        setLiveMessage(
          error instanceof AgentApiError && error.kind === 'cancelled' ? translate({message: '请求已取消。'}) : errorMessage,
        );
      } finally {
        if (requestControllerRef.current === controller) {
          requestControllerRef.current = null;
          setIsSending(false);
        }
      }
    },
    [hasConsented],
  );

  const sendDraft = useCallback(
    async () => sendQuestion(conversationRef.current.draft),
    [sendQuestion],
  );

  const sendSuggestion = useCallback(async (question: string) => sendQuestion(question), [sendQuestion]);

  const retryMessage = useCallback(
    async (messageId: string) => {
      const message = conversationRef.current.messages.find(
        (item) => item.id === messageId && item.role === 'user',
      );
      if (message) {
        await sendQuestion(message.content, message.id);
      }
    },
    [sendQuestion],
  );

  const cancelRequest = useCallback(() => {
    requestControllerRef.current?.abort();
  }, []);

  const resetConversation = useCallback(() => {
    abortActiveRequest();
    archiveConversation(failPendingMessages(conversationRef.current));
    setValidationError('');
    setLiveMessage(translate({message: '已开始新会话。当前会话已保存到本机记录。'}));
    const next = emptyConversation(conversationRef.current.sourceContext);
    conversationRef.current = next;
    setConversation(next);
    persistActiveConversation(next);
  }, [abortActiveRequest, archiveConversation]);

  const openConversation = useCallback(
    (localId: string) => {
      if (conversationRef.current.localId === localId) {
        return;
      }

      const target = historyRef.current.find((item) => item.localId === localId);
      if (!target) {
        setLiveMessage(translate({message: '找不到这条会话记录。'}));
        return;
      }

      abortActiveRequest();
      archiveConversation(failPendingMessages(conversationRef.current));
      setValidationError('');
      const next: ConversationState = {
        localId: target.localId,
        sessionId: target.sessionId,
        messages: target.messages,
        draft: target.draft,
        sourceContext: target.sourceContext ?? conversationRef.current.sourceContext,
      };
      conversationRef.current = next;
      setConversation(next);
      persistActiveConversation(next);
      setLiveMessage(translate({message: '已打开会话：{value0}'}, {value0: target.title}));
    },
    [abortActiveRequest, archiveConversation],
  );

  const deleteConversation = useCallback(
    (localId: string) => {
      const nextHistory = persistHistory(removeHistoryConversation(historyRef.current, localId));
      historyRef.current = nextHistory;
      setHistory(nextHistory);

      if (conversationRef.current.localId !== localId) {
        setLiveMessage(translate({message: '已删除本机会话记录。'}));
        return;
      }

      abortActiveRequest();
      setValidationError('');
      const next = emptyConversation(conversationRef.current.sourceContext);
      conversationRef.current = next;
      setConversation(next);
      persistActiveConversation(next);
      setLiveMessage(translate({message: '已删除当前会话，并开始新会话。'}));
    },
    [abortActiveRequest],
  );

  const sendFeedback = useCallback(
    async (messageId: string, rating: FeedbackRating, reason?: FeedbackReason) => {
      if (!hasConsented) {
        return;
      }

      const target = conversationRef.current.messages.find(
        (message) => message.id === messageId && message.role === 'assistant' && message.messageId,
      );
      if (!target?.messageId || target.feedback?.status === 'submitting') {
        return;
      }

      const feedback: MessageFeedback = {rating, reason, status: 'submitting'};
      setConversation((current) => ({
        ...current,
        messages: current.messages.map((message) =>
          message.id === messageId ? {...message, feedback} : message,
        ),
      }));

      try {
        await submitAgentFeedback(target.messageId, {rating, ...(reason ? {reason} : {})});
        setConversation((current) => ({
          ...current,
          messages: current.messages.map((message) =>
            message.id === messageId
              ? {...message, feedback: {...feedback, status: 'submitted'}}
              : message,
          ),
        }));
        setLiveMessage(translate({message: '感谢反馈，已记录你的选择。'}));
      } catch (error) {
        const errorMessage = getAgentErrorMessage(error);
        setConversation((current) => ({
          ...current,
          messages: current.messages.map((message) =>
            message.id === messageId
              ? {...message, feedback: {...feedback, status: 'failed', error: errorMessage}}
              : message,
          ),
        }));
        setLiveMessage(translate({message: '反馈提交失败，可以重新尝试。'}));
      }
    },
    [hasConsented],
  );

  const retryFeedback = useCallback(
    async (messageId: string) => {
      const feedback = conversationRef.current.messages.find(
        (message) => message.id === messageId,
      )?.feedback;
      if (feedback) {
        await sendFeedback(messageId, feedback.rating, feedback.reason);
      }
    },
    [sendFeedback],
  );

  const value = useMemo<AssistantContextValue>(
    () => ({
      ...conversation,
      isHydrated,
      hasConsented,
      isSending,
      validationError,
      liveMessage,
      history,
      setDraft,
      setSourceContext,
      acceptConsent,
      sendDraft,
      sendSuggestion,
      retryMessage,
      cancelRequest,
      resetConversation,
      openConversation,
      deleteConversation,
      sendFeedback,
      retryFeedback,
    }),
    [
      acceptConsent,
      cancelRequest,
      conversation,
      deleteConversation,
      hasConsented,
      history,
      isHydrated,
      isSending,
      liveMessage,
      openConversation,
      resetConversation,
      retryFeedback,
      retryMessage,
      sendDraft,
      sendFeedback,
      sendSuggestion,
      setDraft,
      setSourceContext,
      validationError,
    ],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantContextValue {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within AssistantProvider.');
  }
  return context;
}
